import { motion } from 'framer-motion';
import { ArrowUpRight, Star } from 'lucide-react';
import { SITE, STATS, VIDEOS } from '@/content/site';
import { TID } from '@/constants/testIds';

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      {/* Decorative ornament line */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-transparent to-[hsl(var(--hairline))]" aria-hidden />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Copy */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="text-overline">Downtown Vancouver · Burrard St</span>
            <span className="hidden sm:inline-block h-px w-14 bg-[hsl(var(--hairline))]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.05, ease, delay: 0.1 }}
            className="font-display-xl text-[2.75rem] sm:text-6xl lg:text-[5.75rem] leading-[1.02]"
          >
            Natural, lifted lashes
            <br />
            <span className="italic text-[hsl(var(--primary))] font-light">that last 8–12 weeks.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.22 }}
            className="mt-8 max-w-xl text-[0.98rem] lg:text-[1.05rem] text-[hsl(var(--muted-foreground))] leading-[1.75]"
          >
            Authentic YUMI Keratin Lash Lift & InLei Lash Filler® at Prép Beauty Parlour —
            1305 Burrard Street. Certified lash artists since 2014. No glue, no extensions,
            no daily curling.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.34 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid={TID.heroBookBtn}
              href={SITE.booking}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-luxe inline-flex items-center gap-2.5 rounded-full bg-[hsl(var(--warm-ink))] text-[hsl(var(--cream))] px-9 py-4 text-[0.72rem] tracking-[0.24em] uppercase font-light hover:bg-[hsl(var(--primary))] transition-colors duration-700 group"
            >
              Book your lash lift
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.25} />
            </a>
            <a
              data-testid={TID.heroPricingBtn}
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--hairline))] px-9 py-4 text-[0.72rem] tracking-[0.24em] uppercase font-light text-[hsl(var(--foreground))]/70 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-all duration-500"
            >
              Explore pricing
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.46 }}
            className="mt-16 grid grid-cols-2 sm:grid-cols-4 divide-x divide-[hsl(var(--hairline))]"
          >
            {STATS.map((s, i) => (
              <div key={s.label} className={`px-5 first:pl-0 ${i === 0 ? 'sm:pl-0' : ''}`}>
                <div className="font-display text-2xl lg:text-[2rem] tracking-tight tabular whitespace-nowrap text-[hsl(var(--foreground))]">
                  {s.value}
                </div>
                <div className="mt-2 text-[0.62rem] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] font-light">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease }}
            className="relative rounded-[2rem] overflow-hidden bg-[hsl(var(--secondary))] luxe-shadow grain"
          >
            <video
              src={VIDEOS.hero}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={VIDEOS.heroPoster}
              className="w-full h-[440px] sm:h-[540px] lg:h-[660px] object-cover"
              data-testid="hero-video"
            />
            {/* subtle bottom gradient for legibility */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[hsl(var(--warm-ink))]/35 to-transparent pointer-events-none" />
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--cream))]/92 backdrop-blur px-3.5 py-1.5 text-[0.65rem] tracking-[0.22em] uppercase font-light">
              <Star className="w-3 h-3 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" strokeWidth={1.25} />
              5.0 · 3,500+ Fresha Reviews
            </div>
            <div className="absolute bottom-5 right-5 max-w-[220px] rounded-2xl bg-[hsl(var(--cream))]/95 backdrop-blur px-5 py-4">
              <div className="text-eyebrow mb-1.5 !text-[hsl(var(--primary))]">Certified</div>
              <div className="font-display text-lg leading-tight tracking-tight">
                YUMI + InLei
              </div>
              <div className="text-[0.7rem] text-[hsl(var(--muted-foreground))] mt-1 tracking-wide">
                Lash artists since 2014
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-y border-[hsl(var(--hairline))]/80 bg-[hsl(var(--cream-2))]/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[0.65rem] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] font-light">
          <span>YUMI · France · Certified</span>
          <span className="inline-block w-1 h-1 rounded-full bg-[hsl(var(--hairline))]" aria-hidden />
          <span>InLei · Italy · Certified</span>
          <span className="inline-block w-1 h-1 rounded-full bg-[hsl(var(--hairline))]" aria-hidden />
          <span>Lash Artists Since 2014</span>
          <span className="inline-block w-1 h-1 rounded-full bg-[hsl(var(--hairline))]" aria-hidden />
          <span>1305 Burrard · Downtown Vancouver</span>
        </div>
      </div>
    </section>
  );
}
